<template>
    <BaseEdge :id="id" :style="edgeComputedStyle" style="stroke-width: 1.5;" :path="path[0]" :marker-end="markerEnd"
        :class="debugEdgeClasses" />

    <EdgeLabelRenderer>
        <div v-if="!lockFlowCreatorStatus && !isViewMode && !isDebugMode" @mouseover="handleMouseEnter" @mouseleave="handleMouseLeave" :style="{
            pointerEvents: 'all',
            opacity: showDeleteButton ? 1 : 0,
            transition: 'opacity 0.3s ease-in-out',
            position: 'absolute',
            transform: `translate(-50%, -50%) translate(${path[1]}px,${path[2]}px)`,
        }" class="nodrag nopan">
            <button class="edgebutton" @click="handleRemoveEdge"><v-icon size="16">mdi-close</v-icon></button>
        </div>
    </EdgeLabelRenderer>
</template>

<script>
import { BaseEdge, EdgeLabelRenderer, getBezierPath, getSmoothStepPath, useVueFlow } from '@vue-flow/core'

export default {
    inheritAttrs: false,
    components: {
        BaseEdge,
        EdgeLabelRenderer
    },
    inject: {
        lockFlowCreatorStatus: { default: false },
        isDebugMode: { default: false },
        debugEdgeStates: { default: () => ({}) }
    },
    props: {
        id: {
            type: String,
            required: true,
        },
        sourceX: {
            type: Number,
            required: true,
        },
        sourceY: {
            type: Number,
            required: true,
        },
        targetX: {
            type: Number,
            required: true,
        },
        targetY: {
            type: Number,
            required: true,
        },
        sourcePosition: {
            type: String,
            required: true,
        },
        targetPosition: {
            type: String,
            required: true,
        },
        markerEnd: {
            type: String,
            required: false,
        },
        style: {
            type: Object,
            required: false,
        },
        source: {
            type: String,
            required: false,
        },
        target: {
            type: String,
            required: false,
        },
        data: {
            type: Object,
            required: false,
        }
    },
    setup(props) {
        const vueFlowInstance = useVueFlow();
        const { removeEdges } = vueFlowInstance;
        
        const handleRemoveEdge = () => {
            removeEdges(props.id);
        };
        
        return { 
            handleRemoveEdge, 
            vueFlowInstance
        };
    },
    data() {
        return {
            showDeleteButton: false,
            mouseOverButton: false,
        };
    },
    watch: {
        style: {
            handler(newStyle) {
                if (this.mouseOverButton) return;

                if (newStyle.strokeWidth == 1.6) {
                    this.showDeleteButton = true;
                } else {
                    setTimeout(() => {
                        if (!this.mouseOverButton) {
                            this.showDeleteButton = false;
                        }
                    }, 300);
                }
            },
            deep: true,
        },
        mouseOverButton(newValue) {
            if (newValue) {
                this.showDeleteButton = true;
            } else {
                setTimeout(() => {
                    if (!this.mouseOverButton) {
                        this.showDeleteButton = false;
                    }
                }, 300);
            }
        }
    },
    mounted() {
        // console.log(this.lockFlowCreatorStatus)  // จะได้ true/false
    },
    computed: {
        isViewMode() {
            return this.$route?.query?.mode === 'view';
        },
        path() {
            // Knowledge nodes ที่ต้องใช้ Bezier curve แทน SmoothStep
            // 'taxPlanningAgent' อยู่ในลิสต์นี้ด้วยเพราะมันคือ agent ชนิดหนึ่ง — ต่อที่ handle เดียวกัน
            // ถูกเลือกด้วยกลไกเดียวกัน เส้นเชื่อมจึงต้องหน้าตาเหมือนกัน ไม่งั้นมันจะไปตกที่ logic
            // "targetX < sourceX ⇒ SmoothStep" แล้วกลายเป็นเส้นหักฉากตัวเดียวในแถวเดียวกัน
            const knowledgeNodeTypes = ['documents', 'databaseIntegration', 'products', 'intentAI', 'apiknowledge', 'sharepoint', 'agent', 'taxPlanningAgent', 'dynamicKnowledge', 'metaDocuments', 'mutualFund'];
            
            try {
                // ตรวจสอบว่า target node เป็น Knowledge node หรือไม่
                const targetNode = this.vueFlowInstance.nodes.value.find(node => node.id === this.target);
                const isTargetKnowledgeNode = targetNode && knowledgeNodeTypes.includes(targetNode.data?.type);
                
                // ตรวจสอบว่า source node เป็น Knowledge node หรือไม่
                const sourceNode = this.vueFlowInstance.nodes.value.find(node => node.id === this.source);
                const isSourceKnowledgeNode = sourceNode && knowledgeNodeTypes.includes(sourceNode.data?.type);
                
                // ถ้า source หรือ target เป็น Knowledge node ให้ใช้ Bezier curve
                if (isTargetKnowledgeNode || isSourceKnowledgeNode) {
                    return getBezierPath(this);
                }
            } catch (error) {
                console.log("Error accessing nodes:", error);
                // ถ้า error ให้ fallback ไปใช้ logic เดิม
            }
            
            // Logic เดิม: ถ้า targetX < sourceX ใช้ SmoothStep, ไม่งั้นใช้ Bezier
            if (this.targetX < this.sourceX) {
                return getSmoothStepPath(this);
            }
            return getBezierPath(this);
        },
        // Debug: computed style for edge
        edgeComputedStyle() {
            if (!this.isDebugMode || !this.debugEdgeStates) return this.style;
            const edgeState = this.debugEdgeStates[this.id];
            const base = { ...this.style };
            if (edgeState === 'active') {
                base.stroke = '#42a5f5';
                base.strokeWidth = 2.5;
                base.strokeDasharray = '8 4';
            } else if (edgeState === 'completed') {
                base.stroke = '#4caf50';
                base.strokeWidth = 2;
            } else {
                // No state or inactive — dim the edge
                base.stroke = '#42a5f5';
                base.strokeWidth = 1;
                base.opacity = 0.3;
            }
            return base;
        },
        // Debug: CSS classes for edge animation
        debugEdgeClasses() {
            if (!this.isDebugMode || !this.debugEdgeStates) return '';
            const edgeState = this.debugEdgeStates[this.id];
            if (edgeState === 'active') return 'debug-edge-active';
            if (edgeState === 'completed') return 'debug-edge-completed';
            if (this.isDebugMode) return 'debug-edge-inactive';
            return '';
        }
    },
    methods: {
        handleMouseEnter() {
            this.mouseOverButton = true;
        },
        handleMouseLeave() {
            this.mouseOverButton = false;
        }
    }
}
</script>

<style scoped>
.edge-button-container {
    opacity: 1;
    transition: opacity 0.3s ease-in-out;
    z-index: 10;
    /* Added z-index to bring the button on top */
}

.edgebutton {
    width: 24px;
    height: 24px;
    background-color: #ff6b6b;
    border: 2px solid #ffffff;
    border-radius: 50%;
    color: white;
    font-size: 14px;
    font-weight: bold;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
    transition: all 0.2s ease-in-out;
    line-height: 1;
    padding: 0;
    opacity: 1;
}

.edgebutton:hover {
    background-color: #ff5252;
    transform: scale(1.1);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
}

.edgebutton:active {
    transform: scale(0.95);
}

/* Debug Edge Animations */
.debug-edge-active {
    animation: debugEdgeFlow 1s linear infinite;
    stroke-dasharray: 8 4;
}

@keyframes debugEdgeFlow {
    0% { stroke-dashoffset: 24; }
    100% { stroke-dashoffset: 0; }
}

.debug-edge-completed {
    transition: stroke 0.3s ease;
}

.debug-edge-inactive {
    opacity: 0.3;
    transition: opacity 0.3s ease;
}
</style>